/**
 * Dynamic Inquiry Detail Component
 * 
 * Renders inquiry details using AI-generated UI schemas for universal email types,
 * with fallback to standard inquiry detail view for form submissions or legacy data.
 */

import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

// Types for dynamic UI rendering
interface ExtractedField {
  fieldName: string;
  value: any;
  confidence: number;
  sourceText: string;
  fieldType: 'string' | 'date' | 'number' | 'boolean' | 'array' | 'email' | 'phone';
  category: 'contact' | 'incident' | 'legal' | 'medical' | 'broker' | 'custom';
  importance: 'critical' | 'important' | 'optional';
}

interface UIComponent {
  type: 'section' | 'field' | 'grid' | 'list' | 'badge' | 'link' | 'contact-action';
  label: string;
  value?: any;
  props?: Record<string, any>;
  children?: UIComponent[];
  displayCondition?: string;
  importance: 'critical' | 'important' | 'optional';
  order: number;
}

interface UISchema {
  sections: UIComponent[];
  layout: 'standard' | 'compact' | 'detailed';
  theme: 'default' | 'broker-specific';
  metadata: {
    generatedFor: string;
    version: string;
    lastUpdated: string;
  };
}

interface DynamicInquiryData {
  extractedFields: ExtractedField[];
  uiSchema: UISchema;
  sourceClassification: any;
  lastUpdated: string;
}

interface Inquiry {
  id: string;
  source: 'EMAIL' | 'FORM';
  dynamicInquiryData?: DynamicInquiryData;
  aiProcessingMetadata?: any;
  // ... other standard inquiry fields
  [key: string]: any;
}

interface DynamicInquiryDetailProps {
  inquiry: Inquiry;
  onFieldCorrection?: (fieldName: string, oldValue: any, newValue: any) => void;
}

export function DynamicInquiryDetail({ inquiry, onFieldCorrection }: DynamicInquiryDetailProps) {
  // If no dynamic data, fall back to standard view
  if (!inquiry.dynamicInquiryData?.uiSchema || inquiry.source !== 'EMAIL') {
    return null; // Let the parent component handle standard view
  }

  const { extractedFields, uiSchema, sourceClassification } = inquiry.dynamicInquiryData;

  return (
    <div className="space-y-6">
      {/* AI Processing Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <div>
                <p className="text-sm font-semibold text-blue-900">
                  🤖 AI-Processed Email Referral
                </p>
                <p className="text-xs text-blue-700">
                  Source: {sourceClassification.source} • 
                  Confidence: {sourceClassification.confidence}% • 
                  Fields: {extractedFields.length}
                </p>
              </div>
            </div>
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              {uiSchema.metadata.version}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Dynamic Sections */}
      {uiSchema.sections
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((section, index) => (
          <DynamicSection
            key={index}
            section={section}
            extractedFields={extractedFields}
            onFieldCorrection={onFieldCorrection}
          />
        ))}

      {/* AI Metadata (for admins) */}
      {inquiry.aiProcessingMetadata && (
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-sm text-gray-600">AI Processing Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <label className="font-medium text-gray-600">Processing Model</label>
                <p className="text-gray-900">{inquiry.aiProcessingMetadata.processingModel}</p>
              </div>
              <div>
                <label className="font-medium text-gray-600">Processing Time</label>
                <p className="text-gray-900">{inquiry.aiProcessingMetadata.processingTime}ms</p>
              </div>
              <div>
                <label className="font-medium text-gray-600">Extraction Confidence</label>
                <p className="text-gray-900">{inquiry.aiProcessingMetadata.extractionConfidence}%</p>
              </div>
              <div>
                <label className="font-medium text-gray-600">Fields Extracted</label>
                <p className="text-gray-900">{inquiry.aiProcessingMetadata.fieldsExtracted}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DynamicSection({ 
  section, 
  extractedFields, 
  onFieldCorrection 
}: { 
  section: UIComponent; 
  extractedFields: ExtractedField[];
  onFieldCorrection?: (fieldName: string, oldValue: any, newValue: any) => void;
}) {
  // Check display condition if specified
  if (section.displayCondition) {
    // Simple condition evaluation (can be enhanced)
    const hasRequiredField = extractedFields.some(f => 
      section.displayCondition?.includes(f.fieldName) && f.value
    );
    if (!hasRequiredField) return null;
  }

  switch (section.type) {
    case 'section':
      return (
        <Card className={getImportanceStyles(section.importance)}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {section.label}
              {section.importance === 'critical' && (
                <Badge variant="destructive" className="text-xs">Critical</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {section.children
                ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((child, index) => (
                  <DynamicSection
                    key={index}
                    section={child}
                    extractedFields={extractedFields}
                    onFieldCorrection={onFieldCorrection}
                  />
                ))}
            </div>
          </CardContent>
        </Card>
      );

    case 'grid':
      return (
        <div className="grid grid-cols-2 gap-4">
          {section.children?.map((child, index) => (
            <DynamicSection
              key={index}
              section={child}
              extractedFields={extractedFields}
              onFieldCorrection={onFieldCorrection}
            />
          ))}
        </div>
      );

    case 'field':
      const fieldData = extractedFields.find(f => f.fieldName === section.value);
      if (!fieldData || !fieldData.value) return null;

      return (
        <DynamicField
          label={section.label}
          field={fieldData}
          props={section.props}
          onCorrection={onFieldCorrection}
        />
      );

    case 'badge':
      const badgeField = extractedFields.find(f => f.fieldName === section.value);
      if (!badgeField || !badgeField.value) return null;

      return (
        <Badge 
          variant={section.props?.variant || 'default'}
          className={section.props?.className}
        >
          {section.label}: {badgeField.value}
        </Badge>
      );

    case 'link':
      const linkField = extractedFields.find(f => f.fieldName === section.value);
      if (!linkField || !linkField.value) return null;

      return (
        <Button
          variant="outline"
          onClick={() => window.open(linkField.value, '_blank')}
          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
        >
          {section.props?.icon && <span className="mr-2">{section.props.icon}</span>}
          {section.label}
        </Button>
      );

    case 'contact-action':
      const contactField = extractedFields.find(f => f.fieldName === section.value);
      if (!contactField || !contactField.value) return null;

      return (
        <ContactActionField
          label={section.label}
          field={contactField}
        />
      );

    default:
      return null;
  }
}

function DynamicField({ 
  label, 
  field, 
  props, 
  onCorrection 
}: { 
  label: string; 
  field: ExtractedField; 
  props?: Record<string, any>;
  onCorrection?: (fieldName: string, oldValue: any, newValue: any) => void;
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState(field.value);

  const handleSaveCorrection = () => {
    if (editValue !== field.value && onCorrection) {
      onCorrection(field.fieldName, field.value, editValue);
    }
    setIsEditing(false);
  };

  const formatValue = (value: any, format?: string) => {
    if (!value) return 'N/A';

    switch (format) {
      case 'phone':
        return formatPhoneNumber(value);
      case 'email':
        return value;
      case 'date':
        return formatDate(value);
      case 'currency':
        return formatCurrency(value);
      default:
        return Array.isArray(value) ? value.join(', ') : String(value);
    }
  };

  return (
    <div className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">{label}</label>
          {field.confidence < 80 && (
            <Badge variant="warning" className="text-xs">
              {field.confidence}% confidence
            </Badge>
          )}
        </div>
        
        {isEditing ? (
          <div className="mt-1 flex items-center gap-2">
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="text-sm px-2 py-1 border border-gray-300 rounded"
            />
            <Button size="sm" onClick={handleSaveCorrection}>Save</Button>
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
          </div>
        ) : (
          <div className="mt-1 flex items-center gap-2">
            <p className="text-sm text-gray-900">
              {formatValue(field.value, props?.format)}
            </p>
            {onCorrection && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsEditing(true)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Edit
              </Button>
            )}
          </div>
        )}
        
        {field.sourceText && field.sourceText !== field.value && (
          <p className="text-xs text-gray-400 mt-1 italic">
            Source: "{field.sourceText.substring(0, 100)}..."
          </p>
        )}
      </div>
    </div>
  );
}

function ContactActionField({ 
  label, 
  field 
}: { 
  label: string; 
  field: ExtractedField; 
}) {
  const handleAction = () => {
    if (field.fieldType === 'phone') {
      window.open(`tel:${field.value}`, '_self');
    } else if (field.fieldType === 'email') {
      window.open(`mailto:${field.value}`, '_self');
    }
  };

  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <label className="text-sm font-medium text-gray-600">{label}</label>
        <p className="text-sm text-gray-900">{field.value}</p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleAction}
        className="text-blue-600 hover:text-blue-800"
      >
        {field.fieldType === 'phone' ? '📞 Call' : '✉️ Email'}
      </Button>
    </div>
  );
}

// Helper functions
function getImportanceStyles(importance: string): string {
  switch (importance) {
    case 'critical':
      return 'border-red-200 bg-red-50';
    case 'important':
      return 'border-orange-200 bg-orange-50';
    default:
      return 'border-gray-200';
  }
}

function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}

function formatDate(date: string): string {
  try {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return date;
  }
}

function formatCurrency(amount: string | number): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}